import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useDisclosure, useColorMode } from "@chakra-ui/react";
import { debounce } from "lodash";
import usePersistedState from "utilities/usePersistedState";
import {
  useGetVideosQuery,
  useGetVideosByQueryMutation,
  useGetPathsQuery,
  useAddSegmentMutation,
  useUpdateSegmentMutation,
  useDeleteSegmentMutation,
} from "services/api";
import useExportVideo from "./useExportVideo";
import useExportXml from "./useExportXml";

const useProject = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { search } = location;
  const params = Object.fromEntries(new URLSearchParams(search));
  const searchQuery = params.query;
  const selectedPath = params.path || null;
  const { colorMode } = useColorMode();

  // Video state
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSegments, setSelectedSegments] = usePersistedState(
    `selectedSegments-${projectId}`,
    []
  );
  const [page, setPage] = useState(1);
  const [allVideos, setAllVideos] = useState([]);
  const pageLimit = 20;

  // UI state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Chakra disclosures
  const {
    isOpen: isVideoOpen,
    onOpen: onVideoOpen,
    onClose: onVideoClose,
  } = useDisclosure();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isChatOpen,
    onOpen: onChatOpen,
    onClose: onChatClose,
  } = useDisclosure();

  // RTK Query hooks
  const [
    getVideosByQuery,
    { isLoading: isLoadingQuery },
  ] = useGetVideosByQueryMutation();

  const { data, error, isLoading, isFetching } = useGetVideosQuery(
    {
      projectId,
      page,
      pageLimit,
      filter: selectedPath || undefined,
    },
    { skip: !!searchQuery }
  );

  const { data: pathsData } = useGetPathsQuery({ projectId });

  // Segment mutation hooks
  const [addSegment] = useAddSegmentMutation();
  const [updateSegment] = useUpdateSegmentMutation();
  const [deleteSegment] = useDeleteSegmentMutation();

  // Import export hooks
  const {
    isExportingVideo,
    exportVideoProgress,
    exportVideoResult,
    exportVideoError,
    handleExportVideo,
  } = useExportVideo({ projectId, selectedSegments });

  const { isExportingXml, exportXmlError, handleExportXml } = useExportXml({
    projectId,
    selectedSegments,
  });

  // Path selection handler
  const handlePathSelect = useCallback(
    (path) => {
      const newParams = new URLSearchParams(search);
      if (path) {
        newParams.set("path", path);
      } else {
        newParams.delete("path");
      }
      history.push({ search: newParams.toString() });
    },
    [search, history]
  );

  // Reset page when path changes
  useEffect(() => {
    setPage(1);
  }, [selectedPath]);

  // Clear search results when switching back to regular mode
  useEffect(() => {
    if (!searchQuery) {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Update allVideos when not in search mode and we have new data
  useEffect(() => {
    if (!searchQuery && data?.data) {
      setAllVideos((prev) => {
        // If we're on page 1, replace the data
        if (page === 1) {
          return data.data;
        }
        // Otherwise append the new data and deduplicate using Set
        const combinedVideos = [...prev, ...data.data];
        const uniqueVideos = Array.from(
          new Set(combinedVideos.map((video) => JSON.stringify(video)))
        ).map((video) => JSON.parse(video));
        return uniqueVideos;
      });
    }
  }, [searchQuery, data?.data, page]);

  // Perform search query when searchQuery changes
  useEffect(() => {
    const fetchQueryResults = async () => {
      if (searchQuery) {
        const queryResults = await getVideosByQuery({
          projectId,
          query: searchQuery,
          filter: selectedPath || undefined,
        }).unwrap();
        setSearchResults(queryResults.data);
      }
    };
    fetchQueryResults();
  }, [getVideosByQuery, projectId, searchQuery, selectedPath]);

  // Reset search results when path changes
  useEffect(() => {
    if (searchQuery) {
      setSearchResults([]);
    }
  }, [selectedPath, searchQuery]);

  // Video data computed values
  const totalVideos = data?.page_info.total_results ?? 0;
  const totalPages = data?.page_info.total_page ?? 0;
  const hasMore = !searchQuery && page < totalPages;
  const getVideoId = (video) => (searchQuery ? video.video_id : video._id);

  // Current videos to display (either search results or all videos)
  const currentVideos = useMemo(() => {
    return searchQuery ? searchResults : allVideos;
  }, [searchQuery, searchResults, allVideos]);

  // Load more videos handler
  const onLoadMore = useCallback(() => {
    debounce(() => {
      if (!searchQuery && hasMore && !isFetching) {
        setPage((prev) => prev + 1);
      }
    }, 300)();
  }, [searchQuery, hasMore, isFetching]);

  // Video click handler
  const onVideoClick = (videoId) => {
    onVideoOpen();
    setActiveVideoId(videoId);
  };

  // Segment handlers
  const handleAddSegment = async (videoId, segment) => {
    try {
      // Get the filename from the segment data since it's already included
      const filename = segment.filename;
      if (!filename) return;

      await addSegment({
        projectId,
        filename,
        segment: {
          start: segment.start,
          end: segment.end,
          confidence: segment.confidence,
          score: segment.score,
          thumbnailUrl: segment.thumbnailUrl,
          parentId: videoId,
          uuid: segment.uuid,
        },
      });
    } catch (error) {
      console.error("Failed to add segment:", error);
    }
  };

  const handleSegmentUpdate = async (videoId, segmentId, updatedSegment) => {
    try {
      // Get the filename from the segment data since it's already included
      const filename = updatedSegment.filename;
      if (!filename) return;

      await updateSegment({
        projectId,
        filename,
        segmentId,
        segment: {
          start: updatedSegment.start,
          end: updatedSegment.end,
          confidence: updatedSegment.confidence,
          score: updatedSegment.score,
          thumbnailUrl: updatedSegment.thumbnailUrl,
          parentId: updatedSegment.parentId,
          uuid: updatedSegment.uuid,
        },
      });
    } catch (error) {
      console.error("Failed to update segment:", error);
    }
  };

  const handleDeleteSegment = async (videoId, segmentId) => {
    try {
      const filename = allVideos.find((v) => v._id === videoId)?.system_metadata
        ?.filename;
      if (!filename) return;

      await deleteSegment({
        projectId,
        filename,
        segmentId,
      });
    } catch (error) {
      console.error("Failed to delete segment:", error);
    }
  };

  return {
    // Video state
    currentVideos,
    activeVideoId,
    setActiveVideoId,
    selectedSegments,
    setSelectedSegments,
    hasMore,
    getVideoId,
    totalVideos,

    // UI state
    isSidebarCollapsed,
    setIsSidebarCollapsed,

    // Chakra disclosures
    isVideoOpen,
    onVideoOpen,
    onVideoClose,
    isOpen,
    onOpen,
    onClose,
    isChatOpen,
    onChatOpen,
    onChatClose,

    // Loading and error states
    isLoading: isLoading || isLoadingQuery,
    error,

    // Paths
    pathsData,
    selectedPath,
    handlePathSelect,

    // Handlers
    onLoadMore,
    onVideoClick,
    handleAddSegment,
    handleSegmentUpdate,
    handleDeleteSegment,

    // Export functionality
    isExportingVideo,
    exportVideoProgress,
    exportVideoResult,
    exportVideoError,
    handleExportVideo,

    isExportingXml,
    exportXmlError,
    handleExportXml,

    // Project info
    projectId,
    searchQuery,
    colorMode,
  };
};

export default useProject;
